// Presenter Archetype — Share Card image generator (new feature, not part
// of the brief's canonical content). Draws a 1080x1080 branded PNG from
// the result (archetype name(s) + accent color) entirely client-side —
// no backend round-trip needed since nothing here needs to be
// server-verified.

const CARD_SIZE = 1080;

const loadBrandFonts = async () => {
  try {
    await Promise.all([
      document.fonts.load('600 72px "Cormorant Garamond"'),
      document.fonts.load('600 28px "Manrope"'),
      document.fonts.load('400 24px "Manrope"'),
    ]);
    await document.fonts.ready;
  } catch {
    /* Fonts API unsupported — canvas falls back to the browser's default
       serif/sans-serif, which is harmless for a shareable image. */
  }
};

// Wraps `text` into the fewest lines that fit `maxWidth` at `fontSize`,
// shrinking in 6px steps (down to `minSize`) until it fits in <= 3 lines.
const fitLines = (ctx, text, maxWidth, startSize, minSize) => {
  let size = startSize;
  let lines = [];
  while (size >= minSize) {
    ctx.font = `600 ${size}px "Cormorant Garamond"`;
    lines = [];
    let line = "";
    for (const word of text.split(" ")) {
      const candidate = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(candidate).width > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    }
    if (line) lines.push(line);
    if (lines.length <= 3) break;
    size -= 6;
  }
  return { size, lines };
};

/**
 * Draws the card onto `canvas` and returns it. `name` is the archetype
 * (or " + "-joined blend) name, `subtitle` is the profile label / blend
 * tag, `accentColor` is one of `ARCHETYPE_ACCENTS` (the leading
 * archetype's, or the first one for a blend).
 */
export const drawShareCard = async (canvas, { name, subtitle, accentColor, eyebrow }) => {
  canvas.width = CARD_SIZE;
  canvas.height = CARD_SIZE;
  const ctx = canvas.getContext("2d");
  await loadBrandFonts();

  const marginX = 96;
  const maxWidth = CARD_SIZE - marginX * 2;

  ctx.fillStyle = "#1c2b2c";
  ctx.fillRect(0, 0, CARD_SIZE, CARD_SIZE);
  ctx.fillStyle = accentColor;
  ctx.fillRect(0, 0, CARD_SIZE, 16);

  ctx.globalAlpha = 0.1;
  ctx.fillStyle = accentColor;
  ctx.beginPath();
  ctx.arc(CARD_SIZE - 140, CARD_SIZE - 180, 280, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = accentColor;
  ctx.font = '700 26px "Manrope"';
  ctx.fillText(eyebrow.toUpperCase(), marginX, 150);

  const { size, lines } = fitLines(ctx, name, maxWidth, 84, 44);
  ctx.font = `600 ${size}px "Cormorant Garamond"`;
  ctx.fillStyle = "#f6f4ed";
  const lineHeight = size * 1.18;
  let y = 260;
  lines.forEach((line) => {
    y += lineHeight;
    ctx.fillText(line, marginX, y);
  });

  ctx.font = '500 30px "Manrope"';
  ctx.fillStyle = "#c9c4b4";
  ctx.fillText(subtitle, marginX, y + 56);

  ctx.font = '700 26px "Manrope"';
  ctx.fillStyle = "#f6f4ed";
  ctx.fillText("InterAct Globally", marginX, CARD_SIZE - 90);
  ctx.font = '400 22px "Manrope"';
  ctx.fillStyle = "#9a968a";
  ctx.fillText("interactglobally.com.br", marginX, CARD_SIZE - 56);

  return canvas;
};

export const canvasToBlob = (canvas) =>
  new Promise((resolve) => canvas.toBlob(resolve, "image/png"));

export const canvasToFile = async (canvas, filename) => {
  const blob = await canvasToBlob(canvas);
  return new File([blob], filename, { type: "image/png" });
};

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
